import { ChevronLeft, ChevronRight } from '@untitledui/icons'

import Button from '@components/designSystem/Button'
import Tooltip from '@components/designSystem/Tooltip'
import SingleShelfView from '@components/shelfDetails/SingleShelfView'
import { type ViewType } from '@components/shelfDetails/utils'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { useShelf } from '@state/shelf/queries'

import type { FacingProducer } from './ShelfDetails'
import type { Shelf } from '@state/shelf/types'

interface TripleShelfViewProps {
    type: ViewType
    shelf: Shelf
    onLeftNeighbourClick: () => void
    onRightNeighbourClick: () => void
    facingProducer?: FacingProducer
}

function TripleShelfView({ shelf, type, onLeftNeighbourClick, onRightNeighbourClick, facingProducer }: TripleShelfViewProps) {
  const { t } = useTypedTranslation()

  const { data: rightNeighbourShelf } = useShelf(shelf.rightNeighborShelfId)
  const { data: leftNeighbourShelf } = useShelf(shelf.leftNeighborShelfId)

  const neighbourClassNames = 'pointer-events-none relative h-full opacity-15 size-full'

  return (
    <>
      <div className='relative flex size-full items-center justify-center'>
        {leftNeighbourShelf && (
          <div className={neighbourClassNames}>
            <SingleShelfView type={type} shelf={leftNeighbourShelf} facingProducer={facingProducer} />
          </div>
        )}
        <div className='relative z-10 size-full'>
          <SingleShelfView type={type} shelf={shelf} facingProducer={facingProducer} />
        </div>
        {rightNeighbourShelf && (
          <div className={neighbourClassNames}>
            <SingleShelfView type={type} shelf={rightNeighbourShelf} facingProducer={facingProducer} />
          </div>
        )}
        {/* Left edge gradient fade */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-linear-to-r from-tertiary to-transparent" />
        {/* Right edge gradient fade */}
        <div className={`
          pointer-events-none absolute inset-y-0 right-0 w-32 bg-linear-to-l from-tertiary to-transparent
        `} />
      </div>

      {
        leftNeighbourShelf && (
          <Tooltip title={t('storeMap.shelf.title', { name: leftNeighbourShelf.name })} placement='right'>
            <Button
              size='xl'
              className='absolute top-1/2 left-6 z-20'
              variant='outline'
              iconLeading={ChevronLeft}
              aria-label={t('storeMap.shelf.navigateToLeftNeighbour')}
              onClick={onLeftNeighbourClick}
            />
          </Tooltip>
        )
      }
      {
        rightNeighbourShelf && (
          <Tooltip title={t('storeMap.shelf.title', { name: rightNeighbourShelf.name })} placement='left'>
            <Button
              size='xl'
              className='absolute top-1/2 right-6 z-20'
              variant='outline'
              iconLeading={ChevronRight}
              aria-label={t('storeMap.shelf.navigateToRightNeighbour')}
              onClick={onRightNeighbourClick}
            />
          </Tooltip>
        )
      }
    </>
  )
}

export default TripleShelfView